import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter } from 'lucide-react';
import { useAuth } from '@/App';

const Footer = () => {
  const { user, profile } = useAuth();
  return (
    <footer className="bg-gray-900 text-gray-400 border-t border-gray-800" data-testid="footer">
      <div className="container-custom px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 mb-4 group">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl sm:rounded-2xl bg-teal-700 flex items-center justify-center shadow-lg group-hover:shadow-teal-700/40 transition-shadow">
                <span className="text-white font-bold text-xs sm:text-sm tracking-tight">act</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base sm:text-lg text-white leading-tight">Talent Collective</span>
                <span className="text-[8px] sm:text-[10px] font-semibold tracking-wide text-teal-400">ACT GRANTS x THE PRODUCT FOLKS</span>
              </div>
            </Link>
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed mb-4 sm:mb-6">
              Connecting exceptional professionals with mission-driven organizations building for India.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/actgrants/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-teal-400 transition-colors"
                aria-label="ACT Grants LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://twitter.com/ACTGrantsIndia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-teal-400 transition-colors"
                aria-label="ACT Grants Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Impact Areas */}
          <div>
            <h4 className="font-semibold text-white mb-3 sm:mb-4 text-xs sm:text-sm">Impact Areas</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              {[
                { href: 'https://actgrants.in/act-for-education/', label: 'Education' },
                { href: 'https://actgrants.in/act-for-health/', label: 'Health' },
                { href: 'https://actgrants.in/act-for-environment/', label: 'Environment' },
                { href: 'https://actgrants.in/act-for-women/', label: "Women's Empowerment" },
              ].map(({ href, label }) => (
                <li key={label}>
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-gray-500 hover:text-teal-400 transition-colors">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* For Professionals */}
          <div>
            <h4 className="font-semibold text-white mb-3 sm:mb-4 text-xs sm:text-sm">For Professionals</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              {[
                { to: '/projects', label: 'Browse Opportunities' },
                { to: user && profile ? '/profile/edit' : '/signup', label: user && profile ? 'Edit Profile' : 'Create Profile' },
                { to: '/how-it-works', label: 'How It Works' },
                { to: '/about', label: 'About Us' },
              ].map(({ to, label }) => (
                <li key={label}>
                  <Link to={to} className="text-xs sm:text-sm text-gray-500 hover:text-teal-400 transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-white mb-3 sm:mb-4 text-xs sm:text-sm">Resources</h4>
            <ul className="space-y-2 sm:space-y-2.5">
              {[
                { href: 'https://actgrants.in', label: 'ACT Grants Website', external: true },
                { to: '/terms', label: 'Terms of Service' },
                { to: '/privacy', label: 'Privacy Policy' },
              ].map((item) => (
                <li key={item.label}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-gray-500 hover:text-teal-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.to} className="text-xs sm:text-sm text-gray-500 hover:text-teal-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs sm:text-sm text-gray-600 text-center md:text-left">
            &copy; {new Date().getFullYear()} Talent Collective. An ACT Grants x The Product Folks initiative.
          </p>
          <p className="text-sm text-gray-600">
            Built with purpose for India's social impact ecosystem.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
